import { useMaestroStore } from '../store/maestroStore';
import type { PlaybackState } from '../protocols/BATON';
import { TransportCommand } from './TransportCommand';
import { detectPlatform } from '../utils/platform';

type ConnectorModule = typeof import('../lib/connector');

class TransportStateImpl {
  private _initialized = false;
  private _unlistenTransport: (() => void) | null = null;
  private _unlistenVolume: (() => void) | null = null;
  private _unlistenPosition: (() => void) | null = null;
  private _unsubscribeStore: (() => void) | null = null;
  private _unsubscribePlaybackPower: (() => void) | null = null;
  private _lastFetchedRendererId: string | null = null;
  private _fetchAttempts: Map<string, number> = new Map();
  private _retryTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly FETCH_MAX_ATTEMPTS = 4;
  private static readonly FETCH_RETRY_DELAYS_MS = [150, 750, 2000, 5000];

  private _connector: ConnectorModule | null = null;
  private _iosCheckLogged = false;
  private _wakeHeartbeat = Date.now();
  private _wakeTimer: ReturnType<typeof setInterval> | null = null;

  private async _getConnector(): Promise<ConnectorModule> {
    if (!this._connector) {
      this._connector = await import('../lib/connector');
    }
    return this._connector;
  }

  init() {
    if (this._initialized) return;
    this._initialized = true;

    if (this._isTauri()) {
      this._listenTauri();
      this._watchPlaybackPowerManagement();
      this._startWakeRecovery();
    }

    if (this._isIOS()) {
      this._listenIOS();
    }

    this._watchActiveRenderer();
  }

  // Watch for activeConnectorRenderer id transitions (zone switch OR initial restore
  // from localStorage) and immediately fetch the renderer's current volume.
  // The Rust volume_monitor only emits on change, so it won't re-push an unchanged
  // cached value — we have to ask explicitly.
  private _watchActiveRenderer() {
    const fetchIfNeeded = () => {
      const state = useMaestroStore.getState();
      const id = state.activeConnectorRenderer?.id ?? null;
      if (!id) return;

      // Check fixed-volume using BOTH activeZone AND discoveredZones — the
      // activeZone may be a partial shim from the sync handler before the
      // full zone reconciliation lands.
      const activeZone = state.activeZone as any;
      const zoneIsFixed = !!activeZone?.capabilities?.fixedVolume;
      const fromList = state.discovery?.discoveredZones?.find((z: any) => z.id === id);
      const listIsFixed = !!fromList?.capabilities?.fixedVolume;
      if (zoneIsFixed || listIsFixed) {
        // Mark as handled so we don't keep retrying — fixed-volume devices
        // genuinely have no queryable volume.
        this._lastFetchedRendererId = id;
        this._fetchAttempts.delete(id);
        return;
      }

      if (id === this._lastFetchedRendererId) return;
      this._lastFetchedRendererId = id;
      this._fetchAttempts.set(id, 0);

      // Optimistically hydrate from the per-renderer cache so the slider shows
      // a plausible value even before the IPC fetch resolves.
      const cached = this._readVolumeCache(id);
      if (cached != null && state.volume == null) {
        state.setVolume(cached);
      }

      this._scheduleFetch(id);
    };

    fetchIfNeeded();
    this._unsubscribeStore = useMaestroStore.subscribe(() => {
      fetchIfNeeded();
      this._persistActiveVolume();
    });
  }

  private _scheduleFetch(rendererId: string): void {
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    const attempt = this._fetchAttempts.get(rendererId) ?? 0;
    if (attempt >= TransportStateImpl.FETCH_MAX_ATTEMPTS) return;
    const delay = TransportStateImpl.FETCH_RETRY_DELAYS_MS[attempt] ?? 5000;
    this._retryTimer = setTimeout(() => {
      this._retryTimer = null;
      // Abort if the active renderer has changed in the meantime.
      const currentId = useMaestroStore.getState().activeConnectorRenderer?.id ?? null;
      if (currentId !== rendererId) return;
      this._fetchRendererVolume(rendererId);
    }, delay);
  }

  private _fetchRendererVolume(rendererId: string): void {
    const p = detectPlatform();
    const isTauri = p.isTauriDesktop;
    const native = p.isIOSNativeApp ? (window as any).__maestroNative : null;
    const attempt = (this._fetchAttempts.get(rendererId) ?? 0) + 1;
    this._fetchAttempts.set(rendererId, attempt);

    if (isTauri) {
      this._getConnector()
        .then(({ getVolume }) =>
          getVolume(rendererId).then((vol) => {
            console.log('[TransportState] Volume fetch OK:', rendererId, vol, `(attempt ${attempt})`);
            useMaestroStore.getState().setVolume(vol);
            this._writeVolumeCache(rendererId, vol);
            this._fetchAttempts.delete(rendererId);
          })
        )
        .catch((e) => {
          console.debug(`[TransportState] get_volume failed attempt ${attempt}/${TransportStateImpl.FETCH_MAX_ATTEMPTS}:`, e);
          // Retry with backoff — RAAT devices sometimes aren't ready to answer
          // immediately after zone switch, or the TCP socket is in-use by another
          // command. Without retry, the PMG slider stays stuck at the cached value.
          if (attempt < TransportStateImpl.FETCH_MAX_ATTEMPTS) {
            this._scheduleFetch(rendererId);
          }
        });
    } else if (native?.getVolume) {
      try {
        native.getVolume(rendererId);
      } catch {}
    }
  }

  // ── Per-renderer volume cache (localStorage) ──
  private static readonly VOLUME_CACHE_KEY = 'maestro_renderer_volumes';

  private _readVolumeCache(rendererId: string): number | null {
    try {
      const raw = localStorage.getItem(TransportStateImpl.VOLUME_CACHE_KEY);
      if (!raw) return null;
      const map = JSON.parse(raw);
      const v = map?.[rendererId];
      return typeof v === 'number' ? v : null;
    } catch {
      return null;
    }
  }

  private _writeVolumeCache(rendererId: string, vol: number): void {
    try {
      const raw = localStorage.getItem(TransportStateImpl.VOLUME_CACHE_KEY);
      const map = raw ? JSON.parse(raw) : {};
      map[rendererId] = vol;
      localStorage.setItem(TransportStateImpl.VOLUME_CACHE_KEY, JSON.stringify(map));
    } catch {}
  }

  private _persistActiveVolume(): void {
    const state = useMaestroStore.getState();
    const id = state.activeConnectorRenderer?.id;
    if (id && typeof state.volume === 'number') {
      this._writeVolumeCache(id, state.volume);
    }
  }

  handleBatonEvent(event: string, payload: any) {
    const store = useMaestroStore.getState();
    switch (event) {
      case 'playbackStateChanged':
        if (
          payload.state === 'paused'
          && TransportCommand.isInPlayUriPostSuccessSilenceWindow()
        ) {
          console.log('[Sync] Ignoring stale pause — within 2s of playUri');
          break;
        }
        if (
          payload.state === 'paused'
          && store.rendererHasStream
          && (store.playbackState === 'playing' || store.playbackState === 'loading')
        ) {
          console.log('[TransportState] Ignoring BATON playbackStateChanged pause — renderer stream');
          break;
        }
        store.setPlaybackState(payload.state);
        break;
      case 'positionChanged':
        store.updateTime(payload.position, payload.duration);
        break;
      case 'trackChanged':
        store.setCurrentTrack(payload.track);
        if (payload.duration) store.setDuration(payload.duration);
        break;
      case 'volumeChanged':
        if (!TransportCommand.isVolumeEchoSuppressed(payload.level)) {
          store.setVolume(payload.level);
        }
        break;
    }
  }

  syncFromServer(serverState: {
    playbackState: string;
    currentTrack: any;
    position: number;
    duration: number;
    volume: number;
    queueIndex: number;
  }) {
    const store = useMaestroStore.getState();
    store.setPlaybackState(serverState.playbackState as any);
    store.setCurrentTrack(serverState.currentTrack);
    store.updateTime(serverState.position, serverState.duration);
    store.setVolume(serverState.volume);
    store.setQueueIndex(serverState.queueIndex);
  }

  destroy() {
    this._unlistenTransport?.();
    this._unlistenVolume?.();
    this._unlistenPosition?.();
    this._unsubscribePlaybackPower?.();
    this._unsubscribePlaybackPower = null;
    this._unsubscribeStore?.();
    this._unsubscribeStore = null;
    this._lastFetchedRendererId = null;
    this._initialized = false;
  }

  /** Connector desktop: sleep prevention follows store playbackState.
   *  Network renderers get full sleep prevention (including lid close);
   *  local/USB playback gets idle-only prevention. */
  private _watchPlaybackPowerManagement() {
    if (!this._isTauri()) return;
    let prev: PlaybackState = useMaestroStore.getState().playbackState;
    let prevZoneId: string | null = null;
    const NETWORK_TRANSPORTS = ['upnp', 'bluesound', 'openhome', 'raat', 'sonos', 'airplay', 'chromecast'];

    const sync = () => {
      const state = useMaestroStore.getState();
      const next = state.playbackState;
      const zoneId = (state.discovery?.activeZone as any)?.id || state.activeZone || null;
      const zoneChanged = zoneId !== prevZoneId;
      if (next === prev && !zoneChanged) return;
      prev = next;
      prevZoneId = zoneId;

      void this._getConnector().then(({ holdAwake, holdAwakeNetwork, releaseAwake }) => {
        if (next !== 'playing') {
          void releaseAwake();
          return;
        }
        const az = state.discovery?.activeZone as any;
        const transportType = (az?.transport_type || az?.type || '').toLowerCase();
        const isNetwork = NETWORK_TRANSPORTS.includes(transportType);
        if (isNetwork) {
          void holdAwakeNetwork();
        } else {
          void holdAwake();
        }
      });
    };
    sync();
    this._unsubscribePlaybackPower = useMaestroStore.subscribe(() => {
      sync();
    });
  }

  /** Detect system wake by monitoring heartbeat gaps.
   *  If the timer fires after a 15s+ gap (impossible unless the system slept),
   *  attempt recovery: re-assert sleep prevention and verify renderer state. */
  private _startWakeRecovery() {
    const HEARTBEAT_MS = 3000;
    const SLEEP_GAP_MS = 15000;

    this._wakeHeartbeat = Date.now();
    this._wakeTimer = setInterval(() => {
      const now = Date.now();
      const gap = now - this._wakeHeartbeat;
      this._wakeHeartbeat = now;

      if (gap > SLEEP_GAP_MS) {
        console.log(`[WakeRecovery] System wake detected — gap was ${Math.round(gap / 1000)}s`);
        this._handleSystemWake(gap);
      }
    }, HEARTBEAT_MS);
  }

  private async _handleSystemWake(_gapMs: number) {
    const state = useMaestroStore.getState();
    if (state.playbackState !== 'playing') return;

    console.log('[WakeRecovery] Was playing before sleep — recovering');

    // Re-assert sleep prevention (may have been cleared during sleep)
    try {
      const C = await this._getConnector();
      const az = state.discovery?.activeZone as any;
      const transportType = (az?.transport_type || az?.type || '').toLowerCase();
      const NETWORK = ['upnp', 'bluesound', 'openhome', 'raat', 'sonos', 'airplay', 'chromecast'];
      if (NETWORK.includes(transportType)) {
        void C.holdAwakeNetwork();
      } else {
        void C.holdAwake();
      }
    } catch {}

    // Give network 3s to reconnect after wake
    await new Promise(r => setTimeout(r, 3000));

    // Verify renderer state via get_transport_info
    const renderer = useMaestroStore.getState().activeConnectorRenderer;
    if (!renderer?.id) return;

    try {
      const C = await this._getConnector();
      const rawResp: string = await C.getTransportInfo(renderer.id);
      // Parse CurrentTransportState from SOAP XML response
      const match = rawResp.match(/<CurrentTransportState>(.*?)<\/CurrentTransportState>/);
      const rendererState = match?.[1] || 'UNKNOWN';
      console.log('[WakeRecovery] Renderer transport state:', rendererState);

      if (rendererState === 'STOPPED' || rendererState === 'NO_MEDIA_PRESENT') {
        console.log('[WakeRecovery] Track ended during sleep — advancing');
        void TransportCommand.next();
      } else if (rendererState === 'PLAYING') {
        console.log('[WakeRecovery] Renderer still playing — OK');
      }
    } catch (err) {
      console.error('[WakeRecovery] Cannot reach renderer — will recover on next user action:', err);
    }
  }

  private _isTauri(): boolean {
    return detectPlatform().isTauriDesktop;
  }

  private _isIOS(): boolean {
    const result = detectPlatform().isIOSNativeApp;
    if (!this._iosCheckLogged) {
      console.log('[TransportState] _isIOS:', result, '| platform:', detectPlatform().deviceName);
      this._iosCheckLogged = true;
    }
    return result;
  }

  private async _listenTauri() {
    const C = await this._getConnector();

    try {
      this._unlistenTransport = await C.onTransportStateChanged(({ rendererId, state }) => {
        const store = useMaestroStore.getState();
        const activeId = store.activeConnectorRenderer?.id;
        console.log(`[Transport] renderer playback state: ${state} renderer=${rendererId} active=${activeId} storeState=${store.playbackState} ts=${new Date().toISOString()}`);
        if (!activeId || rendererId !== activeId) return;

        if (state === 'PLAYING') {
          store.setPlaybackState('playing');
        } else if (state === 'PAUSED_PLAYBACK') {
          if (TransportCommand.isInPlayUriPostSuccessSilenceWindow()) {
            console.log('[Sync] Ignoring stale pause — within 2s of playUri');
          } else if (
            store.rendererHasStream
            && (store.playbackState === 'playing' || store.playbackState === 'loading')
          ) {
            console.log('[TransportState] Ignoring native PAUSED_PLAYBACK — renderer stream + playing');
          } else {
            store.setPlaybackState('paused');
          }
        } else if (state === 'STOPPED' || state === 'NO_MEDIA_PRESENT') {
          if (TransportCommand.isPlayUriRecent(8000)) {
            console.log('[TransportState] Suppressing STOPPED — playUri in progress');
            return;
          }
          const { position, duration } = store;
          const nearEnd = duration > 0 && position >= duration * 0.8;
          if (nearEnd || position < 5) {
            store.setPlaybackState('stopped');
          }
        }
      });
    } catch {}

    try {
      this._unlistenVolume = await C.onVolumeChanged(({ rendererId, volume }) => {
        const store = useMaestroStore.getState();
        const activeId = store.activeConnectorRenderer?.id;
        if (!activeId || rendererId !== activeId) return;
        if (!TransportCommand.isVolumeEchoSuppressed(volume)) {
          store.setVolume(volume);
        }
      });
    } catch {}
  }

  private _listenIOS() {
    const handleTransport = (event: Event) => {
      const { deviceId: rendererId, state } = (event as CustomEvent).detail || {};
      if (!rendererId || !state) return;
      const store = useMaestroStore.getState();
      const activeId = store.activeConnectorRenderer?.id;
      console.log(`[Transport] renderer playback state: ${state} renderer=${rendererId} active=${activeId} storeState=${store.playbackState} platform=iOS ts=${new Date().toISOString()}`);
      if (!activeId || rendererId !== activeId) return;

      if (state === 'PLAYING') {
        store.setPlaybackState('playing');
      } else if (state === 'PAUSED_PLAYBACK') {
        if (TransportCommand.isInPlayUriPostSuccessSilenceWindow()) {
          console.log('[Sync] Ignoring stale pause — within 2s of playUri');
        } else if (
          store.rendererHasStream
          && (store.playbackState === 'playing' || store.playbackState === 'loading')
        ) {
          console.log('[TransportState] Ignoring iOS PAUSED_PLAYBACK — renderer stream + playing');
        } else {
          store.setPlaybackState('paused');
        }
      } else if (state === 'STOPPED' || state === 'NO_MEDIA_PRESENT') {
        if (TransportCommand.isPlayUriRecent(8000)) {
          console.log('[TransportState] Suppressing STOPPED — playUri in progress');
          return;
        }
        const { position, duration } = store;
        const nearEnd = duration > 0 && position >= duration * 0.8;
        if (nearEnd || position < 5) {
          store.setPlaybackState('stopped');
        }
      }
    };

    const handleVolume = (event: Event) => {
      const { deviceId, volume, level } = (event as CustomEvent).detail || {};
      const vol = volume ?? level;
      const store = useMaestroStore.getState();
      const activeId = store.activeConnectorRenderer?.id;
      if (activeId && deviceId === activeId) {
        if (!TransportCommand.isVolumeEchoSuppressed(vol)) {
          store.setVolume(vol);
        }
      }
    };

    window.addEventListener('maestroTransportState', handleTransport);
    window.addEventListener('maestroVolumeChanged', handleVolume);
  }
}

export const TransportState = new TransportStateImpl();

(window as any).TransportState = TransportState;
